// Google Tag Manager loader for the Client Portal.
//
// The container id is per-account and public/index.html is a single static file shared by every
// tenant, so the snippet is injected at runtime once account settings resolve.

import { sanitizeLocation, sanitizePath, sanitizeReferrer } from "./sanitize";

const CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]+$/;

// Google Tag Manager is a partner integration: the account admin switches the GTM partner on and
// supplies the container id in the GTMID partner setting. The BFF hands back the partner payload
// as-is and the resolution of those two values happens here.
const PARTNER_CODE = "GTM";
const CONTAINER_SETTING_CODE = "GTMID";

// Milestones raised before the container is up — an SSO entry redirects within a second of landing,
// and sign-in/sign-up push and then hard-navigate — are held here and replayed on the next page
// rather than lost. Sanitized payloads only, capped, and gone when the tab closes.
const QUEUE_KEY = "dc_gtm_queue";
const QUEUE_LIMIT = 20;

let _initialized = false;
let _disabled = false;

const readQueue = () => {
    try {
        const stored = sessionStorage.getItem(QUEUE_KEY);
        const parsed = stored ? JSON.parse(stored) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
};

const clearQueue = () => {
    try {
        sessionStorage.removeItem(QUEUE_KEY);
    } catch (e) {
        // Storage unavailable — nothing was queued in the first place.
    }
};

const enqueue = (payload) => {
    try {
        sessionStorage.setItem(QUEUE_KEY, JSON.stringify([...readQueue(), payload].slice(-QUEUE_LIMIT)));
    } catch (e) {
        // Storage unavailable — the milestone is dropped rather than retried.
    }
};

export const isValidContainerId = (containerId) =>
    typeof containerId === "string" && CONTAINER_ID_PATTERN.test(containerId.trim());

/**
 * Reduces the account partner payload to whether Google Tag Manager is on for this account and the
 * container id to load. `data` is null when the account has no GTM partner row at all.
 */
export const resolveGtmConfig = (data) => {
    const enabled = (data?.accountPartners ?? []).some(
        (p) => p?.code?.trim()?.toUpperCase() === PARTNER_CODE && Number(p?.statusId) === 1
    );

    const containerId = enabled
        ? (data?.accountPartnerSettings ?? []).find(
            (s) => s?.settingCode?.trim()?.toUpperCase() === CONTAINER_SETTING_CODE
        )?.value
        : null;

    return { enabled, containerId: containerId ?? "" };
};

/**
 * Marks this account as having no container, so milestones stop being buffered for a load that will
 * never happen. Called once account settings say Google Tag Manager is off or misconfigured.
 */
export const disableGtm = () => {
    _disabled = true;
    clearQueue();
};

// Must be a function expression — gtag's shim pushes `arguments`, which arrow functions lack.
const gtagSet = function (fields) {
    const gtag = function () {
        window.dataLayer.push(arguments);
    };
    gtag("set", fields);
};

/**
 * One-time GTM container load. Mirrors Google's standard snippet.
 * `userId` is the MemberId when the patient is already signed in as the container loads, so the
 * GA4 tag has a user id on the very first hit rather than only from the next login onward.
 */
export const initGtm = (containerId, userId) => {
    if (_initialized || _disabled || !isValidContainerId(containerId)) return;

    const id = containerId.trim();
    window.dataLayer = window.dataLayer || [];

    // The sanitized address goes in on the very first push, before the container script is
    // appended, so the GA4 tag has a clean page_location to read on the page that loads it — not
    // only from the second page view onward. Left unbound, the tag collects location.href itself,
    // which on the booking flow carries the reason, the note and the pharmacy ZIP.
    window.dataLayer.push({
        "gtm.start": new Date().getTime(),
        event: "gtm.js",
        page_path: sanitizePath(window.location.pathname, window.location.search),
        page_location: sanitizeLocation(window.location.pathname, window.location.search),
        ...(userId ? { user_id: userId, member_id: userId } : {}),
    });

    // Google's own tag reads document.location and document.referrer directly for the page_view
    // events it generates, so a data layer value alone never reaches them. A `set` command is the
    // one documented way to hand gtag a replacement it applies to every subsequent event. Queued
    // before the container script below, so it is in place before gtag processes anything.
    // user_id rides along for the same reason: bound only through a container variable it depends on
    // that variable resolving before the initialization tag fires, which it does not on first load.
    gtagSet({
        page_location: sanitizeLocation(window.location.pathname, window.location.search),
        page_referrer: sanitizeReferrer(document.referrer),
        ...(userId ? { user_id: userId } : {}),
    });

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);

    _initialized = true;

    // The container reads whatever is already in the data layer when it loads, so milestones held
    // from before this point (or from the page that redirected here) still reach the tags.
    readQueue().forEach((payload) => window.dataLayer.push(payload));
    clearQueue();
};

export const isGtmReady = () => _initialized;

/** Pushes an already-sanitized payload to the data layer, or holds it until the container loads. */
export const pushToDataLayer = (payload) => {
    if (!payload || _disabled) return;
    if (!_initialized) return enqueue(payload);

    window.dataLayer.push(payload);
};

/**
 * Sets the account-linked id for a patient who signs in after the container is already up. The data
 * layer push is what container variables read; the `set` is what actually reaches GA4's user_id
 * field, and it applies to every subsequent hit.
 */
export const setGtmUser = (userId) => {
    if (!_initialized || _disabled || !userId) return;

    window.dataLayer.push({ user_id: userId, member_id: userId });
    gtagSet({ user_id: userId });
};

/**
 * Clears the account-linked id on logout so the next patient on a shared device is not stitched to
 * the previous one. The anonymous visitor id is intentionally left in place.
 */
export const resetGtmUser = () => {
    if (!_initialized) return;
    // null, not undefined: null is what clears a data layer value and unsets GA4's user_id, while an
    // undefined key can leave the previous patient's id in place — the opposite of the intent here.
    window.dataLayer.push({ event: "dc_user_reset", user_id: null, member_id: null });
    gtagSet({ user_id: null });
};
